// Birthday utilities — match today/tomorrow using local clock.
// Stored format: "YYYY-MM-DD" ; YYYY can be "0000" when year unknown.

export function parseBirthday(s) {
  if (!s || typeof s !== "string") return null;
  const parts = s.split("-");
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((x) => parseInt(x, 10));
  if (!m || !d) return null;
  return { year: y || 0, month: m, day: d };
}

export function birthdayStatus(member) {
  const b = parseBirthday(member?.birthday);
  if (!b) return null;
  const now = new Date();
  const todayM = now.getMonth() + 1;
  const todayD = now.getDate();
  const tomorrow = new Date(now.getTime() + 86400000);
  const tmM = tomorrow.getMonth() + 1;
  const tmD = tomorrow.getDate();

  if (b.month === todayM && b.day === todayD) {
    const age = b.year ? now.getFullYear() - b.year : null;
    return { when: "today", age };
  }
  if (b.month === tmM && b.day === tmD) {
    const age = b.year ? tomorrow.getFullYear() - b.year : null;
    return { when: "tomorrow", age };
  }
  return null;
}

export function formatBirthday(s) {
  const b = parseBirthday(s);
  if (!b) return "";
  const d = new Date(2000, b.month - 1, b.day);
  const base = d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
  return b.year ? `${base}, ${b.year}` : base;
}

export function ageFromBirthday(s) {
  const b = parseBirthday(s);
  if (!b || !b.year) return null;
  const now = new Date();
  let age = now.getFullYear() - b.year;
  const m = now.getMonth() + 1;
  const d = now.getDate();
  if (m < b.month || (m === b.month && d < b.day)) age -= 1;
  return age;
}

export function isYoungKid(member) {
  if (!member || member.role !== "kid") return false;
  const age = ageFromBirthday(member.birthday);
  return age != null && age < 7;
}

// True when the family has at least one kid known to be under 7.
// Drives the "big tiles on open" dashboard layout so small children
// can find themselves without reading. Missing birthdays don't qualify
// — we don't guess ages down for a feature this UX-visible.
export function hasYoungKid(members) {
  return (members || []).some((m) => isYoungKid(m));
}

// True when the family has at least one kid under 7 — gates the
// "Toddler mode" button in the dashboard header. Same threshold as
// `hasYoungKid` (which drives the big-tile layout), so whenever big
// tiles are on, Toddler mode is available too. Missing birthdays on
// kids never qualify (we don't guess ages down).
export function hasToddler(members) {
  return (members || []).some((m) => {
    if (!m || m.role !== "kid") return false;
    const age = ageFromBirthday(m.birthday);
    return age != null && age < 7;
  });
}

// True when no human (non-pet) member is KNOWN to be under 11.
// A kid-role member with NO birthday set is treated as a young kid
// (preserves the bright kids palette) — kids' birthdays are easy to
// forget on first signup, and silently flipping the family into
// teen-mode washed out the whole calendar. Parent-role members with
// no birthday still count as adult (parents rarely fill in their own).
// At least one human must exist. Used to switch the dashboard into a
// more compact, teen/adult-friendly layout and fade the calendar's
// candy pastels so a household of teenagers+adults doesn't feel like
// a kid's birthday card.
export function isTeenPlusFamily(members) {
  const humans = (members || []).filter((m) => m.role !== "pet");
  if (humans.length === 0) return false;
  return humans.every((m) => {
    const age = ageFromBirthday(m.birthday);
    if (age == null) {
      // Missing birthday: count kids as young (forces bright palette),
      // count parents as adult (preserves teen+ fade for adult-only
      // households that never filled in birthdays).
      return m.role !== "kid";
    }
    return age >= 11;
  });
}
